import React, { useState, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, X, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';
import { Progress } from './ui/progress.tsx';
import { cn } from '../lib/utils.ts';
import { UploadProgress } from '../types/index.ts';
import { documentApi } from '../services/api.ts';

interface DocumentUploadProps {
  onDocumentsUploaded: (docs: Array<{ documentId: string; filename: string }>) => void;
  onAnalyzeDocumentation: () => void;
  onBack: () => void;
  template: 'product_guide' | 'quickstart' | 'troubleshooting';
  sourceKind: 'manual' | 'webpage';
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onDocumentsUploaded,
  onAnalyzeDocumentation,
  onBack,
  template,
  sourceKind
}) => {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    progress: 0,
    status: 'idle'
  });
  const [uploadedDocs, setUploadedDocs] = useState<Array<{ documentId: string; filename: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [fallbackMessage, setFallbackMessage] = useState<string | null>(null);
  const [webpageUrl, setWebpageUrl] = useState('');
  const [pastedContent, setPastedContent] = useState('');
  const [showPasteFallback, setShowPasteFallback] = useState(false);
  const [bookmarkletCopied, setBookmarkletCopied] = useState(false);
  const [urlLoading, setUrlLoading] = useState(false);

  const isThinExtraction = (sections: Array<{ title?: string; preview?: string }>) => {
    if (sections.length !== 1) return false;
    const title = (sections[0]?.title || '').trim().toUpperCase();
    const preview = (sections[0]?.preview || '').trim();
    const titleOnly = title.startsWith('PAGE TITLE') || title.startsWith('PRODUCT TITLE');
    return titleOnly && preview.length < 220;
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;

    setError(null);

    try {
      // Upload sequentially to keep progress UI simple and predictable.
      for (let i = 0; i < acceptedFiles.length; i += 1) {
        const file = acceptedFiles[i];
        setUploadProgress({
          progress: 0,
          status: 'uploading',
          message: `Uploading ${i + 1}/${acceptedFiles.length}: ${file.name}`
        });

        const response = await documentApi.uploadDocument(file, setUploadProgress);
        if (!response.success) {
          throw new Error(response.message || 'Upload failed');
        }

        setUploadedDocs(prev => {
          const next = [...prev, { documentId: response.data.documentId, filename: file.name }];
          onDocumentsUploaded(next);
          return next;
        });
      }
      setUploadProgress({ progress: 100, status: 'completed', message: 'Upload complete' });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Upload failed');
      setUploadProgress({ progress: 0, status: 'error' });
    }
  }, [onDocumentsUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/markdown': ['.md'],
      'text/plain': ['.txt']
    },
    multiple: true,
    maxFiles: 10,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const clearUpload = () => {
    setUploadProgress({ progress: 0, status: 'idle' });
    setError(null);
    setFallbackMessage(null);
    setUploadedDocs([]);
    setWebpageUrl('');
    setPastedContent('');
    setShowPasteFallback(false);
    onDocumentsUploaded([]);
  };

  const isUploading = uploadProgress.status === 'uploading';
  const isBusy = isUploading || urlLoading;
  const hasError = uploadProgress.status === 'error' || error;
  const hasDocs = uploadedDocs.length > 0;
  const intentLabel = template === 'quickstart' ? 'Quickstart' : 'Product Guide';
  const isWebpageMode = sourceKind === 'webpage';
  const titleLabel = isWebpageMode ? 'Import Webpage for' : 'Upload Manual for';
  const descriptionLabel = isWebpageMode
    ? `Paste a product or help-page URL to generate ${template === 'quickstart' ? 'a focused quickstart' : 'broader product guides'}`
    : `Upload documentation files to generate ${template === 'quickstart' ? 'a focused quickstart' : 'broader product guides'}`;

  const renderFileDropzone = (options?: { compact?: boolean; title?: string; helper?: string }) => {
    const compact = options?.compact ?? false;
    return (
      <div className="space-y-2">
        {options?.title && (
          <div>
            <div className="text-sm font-medium text-gray-700">{options.title}</div>
            {options.helper && <div className="mt-1 text-xs text-muted-foreground">{options.helper}</div>}
          </div>
        )}
        <div
          {...getRootProps()}
          className={cn(
            'border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors',
            compact ? 'p-5' : 'p-8',
            isDragActive ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50',
            isBusy && 'pointer-events-none opacity-50',
            hasError && 'border-destructive'
          )}
        >
          <input {...getInputProps({ 'aria-label': 'Upload documentation files' })} />
          <div className="space-y-2">
            <File className="h-8 w-8 mx-auto text-muted-foreground" />
            {isDragActive ? (
              <p className="text-sm text-primary">Drop your files here...</p>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground">
                  {compact
                    ? 'Drag in a manual or click to browse'
                    : 'Drag and drop your documentation files here, or click to browse'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Supports PDF, Word, Markdown, and text files (max 10MB each)
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const bookmarkletCode = useMemo(() => {
    // eslint-disable-next-line quotes
    const source = `(function(){var d=document;var clean=function(v){return(v||'').replace(/\\s+/g,' ').trim();};var uniq=function(arr){var seen={};return arr.filter(function(item){if(!item||seen[item])return false;seen[item]=true;return true;});};var sanitizeTitle=function(v){var text=clean(v);if(!text)return'';text=text.replace(/(keyboard shortcuts|tastaturk[üu]rzel).*$/i,'').trim();text=text.replace(/\\s*[-|:]+\\s*(amazon\\..*)$/i,'').trim();return text;};var firstNonEmpty=function(values){for(var i=0;i<values.length;i++){var value=clean(values[i]);if(value)return value;}return'';};var textFrom=function(selector,limit){var out=[];var nodes=d.querySelectorAll(selector);for(var i=0;i<nodes.length&&out.length<(limit||12);i++){var text=clean(nodes[i].innerText||nodes[i].textContent||'');if(text)out.push(text);}return uniq(out);};var textFromContainers=function(selector,limit){var out=[];var nodes=d.querySelectorAll(selector);for(var i=0;i<nodes.length&&out.length<(limit||8);i++){var text=clean(nodes[i].innerText||nodes[i].textContent||'');if(text&&text.length>40)out.push(text);}return uniq(out);};var rowsFromTables=function(limit){var tables=d.querySelectorAll('table');var rows=[];for(var i=0;i<tables.length&&rows.length<(limit||20);i++){var trs=tables[i].querySelectorAll('tr');for(var j=0;j<trs.length&&rows.length<(limit||20);j++){var cells=trs[j].querySelectorAll('th,td');if(cells.length<2)continue;var key=clean(cells[0].innerText||cells[0].textContent||'');var value=clean(Array.prototype.slice.call(cells,1).map(function(cell){return cell.innerText||cell.textContent||'';}).join(' '));if(key&&value)rows.push('- '+key+': '+value);}}return uniq(rows);};var parseJsonLd=function(){var scripts=d.querySelectorAll('script[type="application/ld+json"]');var candidates=[];var walk=function(node){if(!node)return;if(Array.isArray(node)){for(var i=0;i<node.length;i++)walk(node[i]);return;}if(typeof node!=='object')return;candidates.push(node);if(node['@graph'])walk(node['@graph']);if(node.mainEntity)walk(node.mainEntity);if(node.itemListElement)walk(node.itemListElement);if(node.hasVariant)walk(node.hasVariant);if(node.subjectOf)walk(node.subjectOf);};for(var i=0;i<scripts.length;i++){var raw=scripts[i].textContent||scripts[i].innerText||'';if(!raw||raw.length>200000)continue;try{walk(JSON.parse(raw));}catch(e){}}return candidates;};var productNode=function(nodes){for(var i=0;i<nodes.length;i++){var node=nodes[i];var type=node&&node['@type'];var types=Array.isArray(type)?type:[type];for(var j=0;j<types.length;j++){if(String(types[j]||'').toLowerCase()==='product')return node;}}for(var k=0;k<nodes.length;k++){if(nodes[k]&&nodes[k].name)return nodes[k];}return null;};var copyViaTextarea=function(text){try{var ta=d.createElement('textarea');ta.value=text;ta.setAttribute('readonly','');ta.style.position='fixed';ta.style.top='0';ta.style.left='-9999px';ta.style.opacity='0';d.body.appendChild(ta);ta.focus();ta.select();ta.setSelectionRange(0,ta.value.length);var ok=!!(d.execCommand&&d.execCommand('copy'));d.body.removeChild(ta);return ok;}catch(e){return false;}};var promptFallback=function(serialized){window.prompt('Automatic copy was blocked on this page. Copy this SIMPI capture payload manually:',serialized);};var buildPayload=function(){var jsonLdNodes=parseJsonLd();var jsonLdProduct=productNode(jsonLdNodes)||{};var pageTitle=clean(d.title||'');var metaTitle=firstNonEmpty([((d.querySelector('meta[property="og:title"],meta[name="twitter:title"]')||{}).content)||'',jsonLdProduct.name||'']);var title=sanitizeTitle(firstNonEmpty([((d.querySelector('#productTitle,[data-automation-id="product-title"]')||{}).innerText)||'',metaTitle,((d.querySelector('h1')||{}).innerText)||'',pageTitle]));var metaDescription=clean(((d.querySelector('meta[name="description"],meta[property="og:description"]')||{}).content)||'');var bullets=textFrom('#feature-bullets li,#productFactsDesktopExpander li,[data-feature-name="featurebullets"] li,main li,article li',10);var highlights=textFromContainers('#corePrice_feature_div,#poExpander,#feature-bullets',3);var paragraphs=textFromContainers('#productDescription,#bookDescription_feature_div,#aplus,#important-information,#importantInformation_feature_div,main p,article p',8).filter(function(t){return t.length>60;}).slice(0,6);var specs=rowsFromTables(12);var sections=[];if(highlights.length)sections.push('TOP HIGHLIGHTS:\\n'+highlights.join('\\n\\n'));if(bullets.length)sections.push('ABOUT THIS ITEM:\\n'+bullets.map(function(t){return '- '+t;}).join('\\n'));if(paragraphs.length)sections.push('PRODUCT DESCRIPTION:\\n'+paragraphs.join('\\n\\n'));if(specs.length)sections.push('SPECIFICATIONS:\\n'+specs.join('\\n'));var jsonLdDescription=clean(jsonLdProduct.description||'');if(jsonLdDescription&&jsonLdDescription.length>80){sections.push('STRUCTURED DESCRIPTION:\\n'+jsonLdDescription);}if(!sections.length&&metaDescription)sections.push('PRODUCT DESCRIPTION:\\n'+metaDescription);var rawText=sections.join('\\n\\n');if(!rawText){rawText=clean((d.querySelector('main,[role="main"],article,body')||d.body||d.documentElement).innerText||'');}return{title:title||undefined,page_title:pageTitle||undefined,source_url:location.href,raw_text:rawText};};var scorePayload=function(payload){var text=payload&&payload.raw_text?payload.raw_text:'';var score=text.length;var matches=text.match(/\\n[A-Z][A-Z ]+:\\n/g);if(matches)score+=matches.length*250;if(payload&&payload.title)score+=200;return score;};var looksThin=function(payload){var text=payload&&payload.raw_text?payload.raw_text:'';return !text||text.length<700||!/(ABOUT THIS ITEM|PRODUCT DESCRIPTION|SPECIFICATIONS|TOP HIGHLIGHTS|STRUCTURED DESCRIPTION)/.test(text);};var finish=function(payload){var serialized=JSON.stringify(payload,null,2);if(copyViaTextarea(serialized)){alert('SIMPI bookmarklet copied the page content. Return to SIMPI and paste it into the fallback box.');return;}if(navigator.clipboard&&navigator.clipboard.writeText){navigator.clipboard.writeText(serialized).then(function(){alert('SIMPI bookmarklet copied the page content. Return to SIMPI and paste it into the fallback box.');}).catch(function(){promptFallback(serialized);});return;}promptFallback(serialized);};var first=buildPayload();if(looksThin(first)){setTimeout(function(){var second=buildPayload();finish(scorePayload(second)>=scorePayload(first)?second:first);},900);return;}finish(first);})();`;
    return `javascript:${source}`;
  }, []);

  const handleCopyBookmarklet = async () => {
    try {
      await navigator.clipboard.writeText(bookmarkletCode);
      setBookmarkletCopied(true);
      window.setTimeout(() => setBookmarkletCopied(false), 2500);
    } catch {
      setBookmarkletCopied(false);
    }
  };

  const handleUrlSubmit = async () => {
    const trimmed = webpageUrl.trim();
    if (!trimmed) return;

    setError(null);
    setFallbackMessage(null);
    setUrlLoading(true);
    setUploadProgress({
      progress: 0,
      status: 'processing',
      message: 'Fetching webpage and extracting content...'
    });

    try {
      const response = await documentApi.uploadFromUrl(trimmed);
      if (!response.success) {
        throw new Error(response.message || 'URL upload failed');
      }

      const sectionsResponse = await documentApi.getDocumentSections(response.data.documentId);
      const sections = sectionsResponse.data?.sections || [];
      if (isThinExtraction(sections)) {
        await documentApi.deleteDocument(response.data.documentId);
        setShowPasteFallback(true);
        setFallbackMessage('This page import only found a thin title-like result. Paste copied page content below instead.');
        setUploadProgress({ progress: 0, status: 'idle' });
        return;
      }

      let label = trimmed;
      try {
        const parsed = new URL(trimmed);
        label = parsed.hostname.replace(/^www\./, '') || trimmed;
      } catch {
        // keep raw string as fallback label
      }

      setUploadedDocs(prev => {
        const next = [...prev, { documentId: response.data.documentId, filename: label }];
        onDocumentsUploaded(next);
        return next;
      });
      setShowPasteFallback(false);
      setWebpageUrl('');
      setUploadProgress({ progress: 100, status: 'completed', message: 'Webpage imported successfully' });
    } catch (uploadError) {
      const message = uploadError instanceof Error ? uploadError.message : 'URL upload failed';
      setError(message);
      setShowPasteFallback(true);
      setFallbackMessage(
        'The site blocked server-side URL import. Paste copied page content below instead.'
      );
      setUploadProgress({ progress: 0, status: 'error' });
    } finally {
      setUrlLoading(false);
    }
  };

  const handlePasteImport = async () => {
    const trimmedContent = pastedContent.trim();
    if (!trimmedContent) return;

    setError(null);
    setFallbackMessage(null);
    setUrlLoading(true);
    setUploadProgress({
      progress: 0,
      status: 'processing',
      message: 'Importing pasted page content...'
    });

    try {
      let payload = {
        content: trimmedContent,
        source_url: webpageUrl.trim() || undefined,
        page_title: undefined as string | undefined,
        title: undefined as string | undefined
      };

      try {
        const parsed = JSON.parse(trimmedContent) as {
          content?: unknown;
          raw_text?: unknown;
          source_url?: unknown;
          page_title?: unknown;
          title?: unknown;
        };
        const parsedContent = typeof parsed.content === 'string'
          ? parsed.content
          : typeof parsed.raw_text === 'string'
            ? parsed.raw_text
            : '';

        if (parsedContent.trim()) {
          payload = {
            content: parsedContent.trim(),
            source_url: typeof parsed.source_url === 'string' && parsed.source_url.trim()
              ? parsed.source_url.trim()
              : (webpageUrl.trim() || undefined),
            page_title: typeof parsed.page_title === 'string' && parsed.page_title.trim()
              ? parsed.page_title.trim()
              : undefined,
            title: typeof parsed.title === 'string' && parsed.title.trim()
              ? parsed.title.trim()
              : undefined
          };
        }
      } catch {
        // Plain copied text is valid input; JSON is optional.
      }

      const response = await documentApi.importBrowserCapture(payload);
      if (!response.success) {
        throw new Error(response.message || 'Browser capture import failed');
      }

      let label = payload.title || payload.page_title || 'Pasted page content';
      if (!payload.title && !payload.page_title && payload.source_url) {
        try {
          label = new URL(payload.source_url).hostname.replace(/^www\./, '') || label;
        } catch {
          // Keep fallback label
        }
      }

      setUploadedDocs(prev => {
        const next = [...prev, { documentId: response.data.documentId, filename: label }];
        onDocumentsUploaded(next);
        return next;
      });
      setPastedContent('');
      setShowPasteFallback(false);
      setUploadProgress({ progress: 100, status: 'completed', message: 'Pasted page imported successfully' });
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : 'Browser capture import failed');
      setUploadProgress({ progress: 0, status: 'error' });
    } finally {
      setUrlLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          {titleLabel} {intentLabel}
        </CardTitle>
        <CardDescription>
          {descriptionLabel}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isWebpageMode && renderFileDropzone()}

        <div className="space-y-2 rounded-lg border border-gray-200 p-4">
          <label htmlFor="webpage-url" className="text-sm font-medium text-gray-700">
            {isWebpageMode ? 'Product or help-page URL' : 'Webpage URL'}
          </label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              id="webpage-url"
              type="url"
              value={webpageUrl}
              onChange={(e) => setWebpageUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isBusy && webpageUrl.trim()) {
                  void handleUrlSubmit();
                }
              }}
              placeholder="https://www.amazon.com/... or https://your-shopify-store.com/..."
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isBusy}
            />
            <Button
              type="button"
              onClick={() => void handleUrlSubmit()}
              disabled={!webpageUrl.trim() || isBusy}
            >
              Import URL
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {isWebpageMode
              ? 'Use this for product pages, listing pages, help center articles, and manufacturer pages.'
              : 'Optional when the most current product information lives on the web.'}
          </p>
        </div>

        {isWebpageMode && fallbackMessage && (
          <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertCircle className="h-4 w-4" />
            <span>{fallbackMessage}</span>
          </div>
        )}

        {isWebpageMode && showPasteFallback && (
          <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="rounded-md border border-amber-300 bg-white/70 p-3 text-sm text-amber-950">
              <div className="font-medium">Use the SIMPI bookmarklet for cleaner capture</div>
              <div className="mt-1">
                Copy the code once, create a bookmark named <span className="font-medium">SIMPI Import</span>, and use the copied code as the bookmark URL. On a tricky product page, click that bookmark and paste the copied payload here.
              </div>
              <div className="mt-3 flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleCopyBookmarklet()}
                  disabled={isBusy}
                  className="sm:max-w-xs"
                >
                  {bookmarkletCopied ? 'Copied bookmarklet code' : 'Copy bookmarklet code'}
                </Button>
                <p className="text-xs text-amber-900/80">
                  Create the bookmark manually and paste the copied code into its URL field.
                </p>
              </div>
            </div>

            <label htmlFor="pasted-page-content" className="text-sm font-medium text-amber-900">
              Paste copied page content
            </label>
            <textarea
              id="pasted-page-content"
              value={pastedContent}
              onChange={(e) => setPastedContent(e.target.value)}
              placeholder={'If URL import is too thin, open the page in your browser, press Cmd-A, Cmd-C, then paste here.'}
              className="min-h-40 w-full rounded-md border border-amber-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              disabled={isBusy}
            />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-amber-900/80">
                Keep the source URL above if you have it. Plain text or bookmarklet JSON both work.
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowPasteFallback(false);
                    setFallbackMessage(null);
                  }}
                  disabled={isBusy}
                >
                  Hide
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handlePasteImport()}
                  disabled={!pastedContent.trim() || isBusy}
                >
                  Import pasted page
                </Button>
              </div>
            </div>
          </div>
        )}

        {isWebpageMode && renderFileDropzone({
          compact: true,
          title: 'Optional: add a supporting file',
          helper: 'Use this only if you also want to include a manual, spec sheet, or extra documentation.'
        })}

        {/* Uploaded Documents */}
        {hasDocs && (
          <div className="space-y-2">
            {uploadedDocs.map((d) => (
              <div key={d.documentId} className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-700">Document '{d.filename}' uploaded successfully</span>
              </div>
            ))}
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearUpload}
                disabled={isBusy}
                className="h-8"
              >
                <X className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {(isUploading || urlLoading) && (
          <div className="space-y-2">
            <Progress value={uploadProgress.progress} />
            <p className="text-sm text-muted-foreground text-center">
              {uploadProgress.message || 'Uploading...'}
            </p>
          </div>
        )}

        {/* Error Message */}
        {hasError && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive rounded-md">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span className="text-sm text-destructive">{error}</span>
          </div>
        )}

        {/* Analyze */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
          <Button
            variant="outline"
            onClick={onBack}
            disabled={isBusy}
          >
            Back
          </Button>
          <Button
            onClick={onAnalyzeDocumentation}
            className="w-full sm:max-w-xs"
            disabled={!hasDocs || isBusy}
          >
            Analyze Source
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};